export interface WellknownMixpanelEvents {
  $experiment_started: {
    "Experiment name": string;
    "Variant name": string;
  };
  OnMeristemExperimentDecided: {
    experimentName: string;
    variationName: string;
    /** @deprecated Use meristemSha instead. */
    sha: string;
  };
}

type ReferMethod = "SMS" | "EMAIL" | "LINK" | "SHAREAPI";

// NoomBuyflowEvents are sent directly to data lake via kinesis stream.
// Before they end up in data lake, their schema is validated against their
// proto definitions in noom-contracts repo.
//
// To make this process transparent for engineers, buyflow client is the source
// of truth for these events and when this interface is changed and merged to
// master new proto definitions are automatically generated and PR in noom-contracts
// is opened.
export interface NoomBuyflowEvents {
  ReferralSubmitted: {
    referrerGrowthUserId: string;
    referrerAccessCode: string;
    referralMethod: ReferMethod;
    referralEntryPoint: "IN_APP" | "BUYFLOW";
    refereeEmailAddress?: string;
    refereePhoneNumber?: string;
    talkableCampaignId?: string;
  };
  RefereeSignup: {
    refereeGrowthUserId: string;
    refereeAccessCode: string;
    referrerGrowthUserId: string;
    referrerAccessCode: string;
    referralMethod: ReferMethod;
    promoCode?: string;
    talkableCampaignId?: string;
  };
  BuyflowSurveyStarted: {
    surveyName: string;
  };
  BuyflowSurveyFinished: {
    surveyName: string;
  };
  BuyflowQuestionViewed: {
    surveyName: string;
    questionId: string;
    lastQuestionSeen: string;
  };
  BuyflowQuestionAnswered: {
    surveyName: string;
    questionId: string;
    /** For the main answer(s) to the question. Used in the majority of cases. */
    answers?: string[];
    /** For questions that store any additional info, i.e. height and weight values, "other" free-text responses, etc. */
    answerParts?: Record<string, string>;
    lastQuestionSeen: string;
  };
}

// Used to generate proto definitions in scripts/generate-events-proto.ts
export interface NoomBuyflowEventSharedProperties {
  browserFamily: string;
  browserMajorVersion: string;
  geoIpCity: string;
  condensedContextType: string;
  consentFramework: string;
  geoIpContinent: string;
  geoIpCountry: string;
  geoIpSubdivision: string;
  currentUrl: string;
  deviceBrand: string;
  deviceFamily: string;
  deviceIsMobile: boolean;
  fullStorySessionUrl: string;
  buyflowGitSha: string;
  hasBeenVisible: boolean;
  httpUserAgent: string;
  initialUrl: string;
  isUnloading: boolean;
  isVisible: boolean;
  language: string;
  meristemContextType: string;
  meristemExperiments: string[];
  meristemJourney: Record<string, string>;
  meristemSha: string;
  meristemTreatment: string;
  meristemVariations: string[];
  noomPlanId: string;
  osFamily: string;
  osMajor: string;
  pageSet: string;
  referrerUrl: string;
  routeId: string;
  trafficType: string;
  utmCampaign: string;
  utmContent: string;
  utmMedium: string;
  utmSource: string;
  utmTerm: string;
  growthUserId: string;
}

// Events that does not conform to a proto schema (created from
// NoomBuyflowEventSharedProperties interface) will not pass the validation
// in event ingestion lambda.
// NOOM_BUYFLOW_EVENT_SHARED_PROPERTIES is a duplicated list of the properties from
// the interface above used to filter the properties sent to data lake. We need
// this duplication so that we can filter out the invalid properties in runtime
// and typings ensure that this list will always match the interface above.
export const NOOM_BUYFLOW_EVENT_SHARED_PROPERTIES: Record<
  keyof NoomBuyflowEventSharedProperties,
  true
> = {
  browserFamily: true,
  browserMajorVersion: true,
  geoIpCity: true,
  condensedContextType: true,
  consentFramework: true,
  geoIpContinent: true,
  geoIpCountry: true,
  currentUrl: true,
  deviceBrand: true,
  deviceFamily: true,
  deviceIsMobile: true,
  fullStorySessionUrl: true,
  buyflowGitSha: true,
  hasBeenVisible: true,
  httpUserAgent: true,
  initialUrl: true,
  isUnloading: true,
  isVisible: true,
  language: true,
  meristemContextType: true,
  meristemExperiments: true,
  meristemJourney: true,
  meristemSha: true,
  meristemTreatment: true,
  meristemVariations: true,
  noomPlanId: true,
  osFamily: true,
  osMajor: true,
  pageSet: true,
  referrerUrl: true,
  routeId: true,
  geoIpSubdivision: true,
  trafficType: true,
  utmCampaign: true,
  utmContent: true,
  utmMedium: true,
  utmSource: true,
  utmTerm: true,
  growthUserId: true,
};
